import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";

const SocialPostPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);


  useEffect(() => {
    if (!id) {
      alert("Post not found");
      return;
    }

    const fetchPost = async () => {
      try {
        const res = await fetch(`https://portal.eyision.com/api/v1/social/post/${id}`,
          {
            method: "GET",
          },
        );

        if (!res.ok) throw new Error("Post not found");

        const data = await res.json();

        const postData = data.data || data;

        setPost(postData);
        setLoading(false);
      } catch (err) {
        console.error("Error:", err);
        setLoading(false);
      }
    };

    fetchPost();
  }, [id]);

  useEffect(() => {
    if (!post) return;

    const isCrawler =
      /bot|crawler|spider|facebookexternalhit|whatsapp|twitterbot|linkedinbot|slack|telegram|pinterest|reddit/i.test(
        navigator.userAgent,
      );

    if (!isCrawler) {
      const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

      if (isMobile) {
        // Try to open app
        const timeout = setTimeout(() => {
          const isAndroid = /Android/i.test(navigator.userAgent);
          if (isAndroid) {
            window.location.href =
              "https://play.google.com/store/apps/details?id=com.app.eysion";
          } else {
            window.location.href =
              "https://apps.apple.com/in/app/eyision/id6749613401";
          }
        }, 2500);

        // Attempt to open app
        window.location.href = `eyision://post/${post._id}`;

        // Clear timeout if page becomes hidden (app opened)
        const handleBlur = () => clearTimeout(timeout);
        window.addEventListener("blur", handleBlur);

        return () => {
          clearTimeout(timeout);
          window.removeEventListener("blur", handleBlur);
        };
      }
    }
  }, [post]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-purple-50 to-pink-50">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
          <p className="mt-4 text-gray-600">Loading post...</p>
        </div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-purple-50 to-pink-50">
        <div className="text-center bg-white rounded-lg p-8 max-w-md mx-4">
          <h1 className="text-2xl font-bold text-red-600 mb-4">
            Post Not Found
          </h1>
          <p className="text-gray-600 mb-6">
            This post may have been removed or doesn't exist.
          </p>
          <button
            onClick={() => navigate("/")}
            className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
          >
            Go Home
          </button>
        </div>
      </div>
    );
  }

  // Prepare meta data from your actual post structure
  const baseUrl = "https://portal.eyision.com";
  const imageUrl =
    post.image?.[0] || "https://portal.eyision.com/default-og-image.png";
  const fullUrl = `${baseUrl}/social/post/${post._id}`;
  const title = post.title || "Eyision Post";
  const description =
    post.description?.substring(0, 200) || "Check out this post on Eyision";

  // Get user info
  const userName =
    post.user_id?.userName || post.user_id?.full_name || "Someone";
  const userImage = post.user_id?.user_image;

  return (
    <>
      <Helmet>
        <title>{title} | Eyision</title>
        <meta name="description" content={description} />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="article" />
        <meta property="og:url" content={fullUrl} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:image" content={imageUrl} />
        <meta property="og:image:secure_url" content={imageUrl} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content={title} />
        <meta property="og:site_name" content="Eyision" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
        <meta name="twitter:image" content={imageUrl} />

        {/* Additional meta tags */}
        <meta name="author" content={userName} />
        <meta
          property="article:published_time"
          content={new Date(post.updatedAt).toISOString()}
        />
      </Helmet>

      {/* Visual Content - What users see */}
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-purple-50">
        <div className="max-w-4xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
          {/* Post Card */}
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
            {/* Header with user info */}
            <div className="p-4 sm:p-6 border-b border-gray-100">
              <div className="flex items-center gap-3">
                {userImage ? (
                  <img
                    src={userImage}
                    alt={userName}
                    className="w-12 h-12 rounded-full object-cover"
                  />
                ) : (
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-400 to-pink-400 flex items-center justify-center text-white font-bold text-xl">
                    {userName.charAt(0).toUpperCase()}
                  </div>
                )}
                <div>
                  <h3 className="font-semibold text-gray-900">{userName}</h3>
                  <p className="text-sm text-gray-500">
                    {new Date(post.updatedAt).toLocaleDateString()}
                  </p>
                </div>
              </div>
            </div>

            {/* Image Section */}
            {post.image && post.image[0] && (
              <div className="relative bg-black">
                <img
                  src={post.image[0]}
                  alt={title}
                  className="w-full h-auto max-h-[600px] object-contain"
                  loading="eager"
                />
              </div>
            )}

            {/* Content Section */}
            <div className="p-6 sm:p-8">
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">
                {title}
              </h1>

              {description && (
                <div className="text-gray-700 text-base sm:text-lg mb-6 leading-relaxed whitespace-pre-wrap">
                  {post.description}
                </div>
              )}

              {/* Stats */}
              <div className="flex gap-6 mb-6 text-sm text-gray-500">
                {post.likeCount > 0 && (
                  <span className="flex items-center gap-1">
                    ❤️ {post.likeCount}{" "}
                    {post.likeCount === 1 ? "like" : "likes"}
                  </span>
                )}
                {post.shareCount > 0 && (
                  <span className="flex items-center gap-1">
                    🔄 {post.shareCount}{" "}
                    {post.shareCount === 1 ? "share" : "shares"}
                  </span>
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={() => {
                    window.location.href = `eyision://post/${post._id}`;
                  }}
                  className="flex-1 px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white font-semibold rounded-lg hover:from-purple-700 hover:to-pink-700 transition-all transform hover:scale-[1.02]"
                >
                  Open in App
                </button>

                <a
                  href="https://play.google.com/store/apps/details?id=com.app.eysion"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 px-6 py-3 bg-gray-100 text-gray-800 font-semibold rounded-lg hover:bg-gray-200 transition-colors text-center"
                >
                  Download App
                </a>
              </div>

              <div className="mt-6 text-center text-sm text-gray-500">
                <p>Get the Eyision app for the best experience</p>
                <div className="flex gap-4 justify-center mt-2">
                  <a
                    href="https://play.google.com/store/apps/details?id=com.app.eysion"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-purple-600 hover:underline"
                  >
                    📱 Android
                  </a>
                  <span>•</span>
                  <a
                    href="https://apps.apple.com/in/app/eyision/id6749613401"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-purple-600 hover:underline"
                  >
                    📱 iOS
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default SocialPostPage;
